import { useEffect } from 'react'
import type { BackstageNavigator } from '../navigator/Router'
import type { AppDispatch } from '../../../store/store'
import { selectLora } from '../../../store/slices/lorasSlice'
import { setBrushEnabled } from '../../../store/slices/selectionSlice'
import type { MaskActions } from '../../Canvas/MaskContext'
import { useAppSelector } from '../../../store/hooks/hooks'
import { selectActiveSessionId } from '../../../store/selectors/sessionSelectors'

interface UseBackstageEventsProps {
    nav: BackstageNavigator
    dispatch: AppDispatch
    maskActions: MaskActions
    setCollapsed: (collapsed: boolean) => void
    requestCollapse: (next: boolean) => void
    refreshRecommendations: () => void
}

export function useBackstageEvents({
    nav,
    dispatch,
    maskActions,
    setCollapsed,
    requestCollapse,
    refreshRecommendations
}: UseBackstageEventsProps) {
    const activeSessionId = useAppSelector(selectActiveSessionId)
    // Collapse event listener
    useEffect(() => {
        const onCollapse = (e: Event) => {
            const ce = e as CustomEvent<{ collapsed: boolean } | undefined>
            const c = Boolean(ce.detail?.collapsed)
            setCollapsed(c)
        }
        window.addEventListener('thread:collapse', onCollapse as EventListener)
        return () => window.removeEventListener('thread:collapse', onCollapse as EventListener)
    }, [setCollapsed])

    // Brush enable listener
    useEffect(() => {
        const onEnableBrush = (e: Event) => {
            const ce = e as CustomEvent<{ navigate?: string } | undefined>
            if (ce.detail?.navigate === 'lora') {
                requestCollapse(false)
                nav.replace('lora')
            }
        }
        window.addEventListener('canvas:enablebrush', onEnableBrush as EventListener)
        return () => window.removeEventListener('canvas:enablebrush', onEnableBrush as EventListener)
    }, [nav, requestCollapse])

    // Brush exit listener
    useEffect(() => {
        const onExitBrush = () => {
            const isIdentityWorkflow = nav.current === 'lora' || nav.current === 'training'
            if (isIdentityWorkflow) {
                nav.replace('chat')
            }
        }
        window.addEventListener('canvas:exitbrush', onExitBrush as EventListener)
        return () => window.removeEventListener('canvas:exitbrush', onExitBrush as EventListener)
    }, [nav])

    // AI models drag-drop listener
    useEffect(() => {
        const onAiModelDrop = () => {
            if (nav.current === 'ai-models') {
                nav.replace('chat')
            }
            refreshRecommendations()
        }
        window.addEventListener('backstage:ai-model-drop', onAiModelDrop as EventListener)
        return () => window.removeEventListener('backstage:ai-model-drop', onAiModelDrop as EventListener)
    }, [nav, refreshRecommendations])

    // Manage brush mode for identity workflow
    useEffect(() => {
        const isIdentityWorkflow = nav.current === 'lora' || nav.current === 'training'

        if (isIdentityWorkflow) {
            if (activeSessionId) {
                dispatch(setBrushEnabled({ sessionId: activeSessionId, brushEnabled: true }))
            }
        }

        return () => {
            if (isIdentityWorkflow) {
                if (maskActions.resetMask) {
                    maskActions.resetMask()
                }
                dispatch(selectLora(null))
                if (activeSessionId) {
                    dispatch(setBrushEnabled({ sessionId: activeSessionId, brushEnabled: false }))
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nav.current, dispatch, activeSessionId, maskActions.resetMask])
}

